/**
 * What the `fmt_str` a metric declares in `kpi_definitions` says about its
 * display: whether the stored value is a fraction shown as a percent label
 * (a percent type, `{:.2%}`), and with how many decimals.
 *
 * Shared so the temporal grid (`columnUtils`), the summary grid
 * (`summaryPageHelpers`) and the graph (`graphTransforms`) can't drift apart —
 * the same fraction reading "12.35 %" on one screen and "0.12" on the other is
 * a data-trust bug.
 *
 * Keyed on `fmt_str` rather than on `PERCENTAGE` appearing in the metric name:
 * the name is a business identifier no constraint ties to display, and this
 * codebase has already shipped a silent version of that bug — the graph's
 * `CATEGORY_TO_AXIS` expected a `Percentage` metric_category no seed row
 * carries, leaving the percent axis dead. A new ratio KPI now needs a seed
 * row, not a frontend change.
 */

/** Decimals to use when a percent `fmt_str` names none. */
const PERCENT_DECIMALS = 2;

function fmtStr(
  metric: string | undefined,
  formatMapping: Record<string, string> | undefined
): string | undefined {
  return metric ? formatMapping?.[metric] : undefined;
}

export function isPercentageMetric(
  metric: string | undefined,
  formatMapping: Record<string, string> | undefined
): boolean {
  return fmtStr(metric, formatMapping)?.includes("%") ?? false;
}

/**
 * The decimals a `fmt_str` declares — `{:,.0f}` → 0, `{:.2%}` → 2 — or null
 * when it declares none (or the metric has no `fmt_str`).
 */
export function formatDecimals(
  metric: string | undefined,
  formatMapping: Record<string, string> | undefined
): number | null {
  const digits = fmtStr(metric, formatMapping)?.match(/\.(\d+)[f%]/);
  return digits ? Number(digits[1]) : null;
}

/** The decimals a percentage metric's label carries. */
export function percentageDecimals(
  metric: string | undefined,
  formatMapping: Record<string, string> | undefined
): number {
  return formatDecimals(metric, formatMapping) ?? PERCENT_DECIMALS;
}
